// A Storage URL's object key can embed the uploader's file name, so everything after
// the bucket is masked. The optional segments are storage-api route qualifiers.
const STORAGE_OBJECT_URL_PATTERN =
  /^(\/storage\/v1\/(?:object|render\/image)\/(?:(?:info|upload)\/)?(?:(?:public|authenticated|sign)\/)?[^/]+)\/.+/;
// A resumable-upload id is the base64url of `<bucket>/<object key>`.
const STORAGE_UPLOAD_ID_URL_PATTERN = /^(\/storage\/v1\/upload\/resumable(?:\/sign)?)\/.+/;
// Free-text twin. storage-js leaves `?`, `#`, quotes and spaces unencoded in the key,
// so nothing short of a line break reliably ends a file name in text: the path is
// masked to the end of its line. storage-api caps a key at 1024 bytes, within 4096.
const STORAGE_OBJECT_TEXT_PATTERN =
  /(\/storage\/v1\/(?:object|render\/image)\/(?:(?:info|upload)\/)?(?:(?:public|authenticated|sign)\/)?[^\s"#'/<>?]{1,256})\/[^\n\r]{1,4096}/g;
/** Route words the patterns above can backtrack into the bucket slot, as on the
 *  bucket-level `/object/sign/<bucket>` and `/object/list/<bucket>`. */
const STORAGE_ROUTE_WORDS: ReadonlySet<string> = new Set([
  'info',
  'upload',
  'public',
  'authenticated',
  'sign',
  'list',
  'list-v2',
]);
/** storage-api's image-transform params and the values they take. On a masked
 *  Storage URL any other key, or a value outside these shapes, can be the tail of a
 *  file name that an unencoded `?` split off the key (`report?format=Jane.pdf`). */
const STORAGE_TRANSFORM_PARAM_VALUES: ReadonlyMap<string, RegExp> = new Map([
  ['width', /^\d{1,5}$/],
  ['height', /^\d{1,5}$/],
  ['quality', /^\d{1,3}$/],
  ['resize', /^(?:cover|contain|fill)$/],
  ['format', /^(?:origin|avif|webp)$/],
]);
/** Kept whatever their value: `redactParams` replaces it (`SENSITIVE_URL_PARAMS`). */
const STORAGE_REDACTED_PARAMS: ReadonlySet<string> = new Set(['token', 'download']);

/** Replacer for the Storage patterns: `<head>/<rest>` → `<head>/:path`. When the
 *  segment matched as the bucket is a route word, the head stays and only the rest
 *  is scanned again, since it can still hold a real Storage path. */
function maskStorageMatch(match: string, head: string): string {
  const bucket = head.slice(head.lastIndexOf('/') + 1);
  if (!STORAGE_ROUTE_WORDS.has(bucket)) {
    return `${head}/:path`;
  }
  return `${head}${maskStorageTextPaths(match.slice(head.length))}`;
}

/** Mask the object path of a Supabase Storage URL in place
 *  (`/storage/v1/object/<bucket>/<path>` → `/storage/v1/object/<bucket>/:path`).
 *  The fragment and every non-storage-api query param go too: they can only be a
 *  file name's tail. Returns whether it changed. */
export function maskStorageObjectPath(parsed: URL): boolean {
  const masked = parsed.pathname
    .replace(STORAGE_OBJECT_URL_PATTERN, maskStorageMatch)
    .replace(STORAGE_UPLOAD_ID_URL_PATTERN, maskStorageMatch);
  if (masked === parsed.pathname) {
    return false;
  }
  parsed.pathname = masked;
  parsed.hash = '';
  for (const key of new Set(parsed.searchParams.keys())) {
    if (!isStorageQueryParam(key, parsed.searchParams.getAll(key))) {
      parsed.searchParams.delete(key);
    }
  }
  return true;
}

function isStorageQueryParam(key: string, values: readonly string[]): boolean {
  if (STORAGE_REDACTED_PARAMS.has(key)) {
    return true;
  }
  const shape = STORAGE_TRANSFORM_PARAM_VALUES.get(key);
  return shape !== undefined && values.every((value) => shape.test(value));
}

/** Mask every Storage object path in free text, relative or inside an absolute URL,
 *  from the bucket to the end of its line. */
export function maskStorageTextPaths(text: string): string {
  return text.replaceAll(STORAGE_OBJECT_TEXT_PATTERN, maskStorageMatch);
}
